/**
 * Funds and Analytics API service for fund management and performance analytics
 */

import { apiGet, apiPost, apiPut, apiDelete } from './apiClient';
import { PaginatedResponse } from './apiClient';

// Types
export interface Fund {
  id: string;
  name: string;
  fund_type: string;
  inception_date: string;
  currency: string;
  aum: number;
  nav: number;
  nav_date: string;
  status: string;
  manager_id: string;
  manager_name: string;
  domicile: string;
  investment_strategy: string;
  benchmark?: string;
  description?: string;
  risk_profile: string;
  min_investment: number;
  fee_structure: {
    management_fee: number;
    performance_fee: number;
    other_fees: Record<string, number>;
  };
  regulatory_info: {
    isin?: string;
    lei?: string;
    regulatory_status: string;
    reporting_requirements: string[];
  };
  created_at: string;
  updated_at: string;
}

export interface FundPerformance {
  fund_id: string;
  period: string;
  return_gross: number;
  return_net: number;
  benchmark_return?: number;
  volatility: number;
  sharpe_ratio: number;
  max_drawdown: number;
  alpha?: number;
  beta?: number;
  information_ratio?: number;
  tracking_error?: number;
  var_95?: number;
  var_99?: number;
  calculation_date: string;
}

export interface FundHolding {
  id: string;
  fund_id: string;
  asset_id: string;
  asset_name: string;
  asset_type: string;
  sector?: string;
  country?: string;
  currency: string;
  quantity: number;
  market_value: number;
  weight: number;
  cost_basis: number;
  unrealized_pnl: number;
  as_of_date: string;
}

export interface FundAnalytics {
  fund_id: string;
  performance: {
    returns: {
      ytd: number;
      '1m': number;
      '3m': number;
      '6m': number;
      '1y': number;
      '3y': number;
      '5y': number;
      inception: number;
    };
    risk_metrics: {
      volatility: number;
      sharpe_ratio: number;
      max_drawdown: number;
      var_95: number;
      beta: number;
    };
    attribution: {
      asset_allocation: Array<{
        asset_class: string;
        weight: number;
        contribution: number;
      }>;
      sector_allocation: Array<{
        sector: string;
        weight: number;
        contribution: number;
      }>;
      geographic_allocation: Array<{
        country: string;
        weight: number;
        contribution: number;
      }>;
    };
  };
  risk_analysis: {
    concentration_risk: {
      top_10_holdings: number;
      herfindahl_index: number;
      effective_positions: number;
    };
    liquidity_analysis: {
      daily_liquidity: number;
      weekly_liquidity: number;
      monthly_liquidity: number;
    };
    style_analysis: {
      growth_value: number;
      size_bias: number;
      quality_factor: number;
    };
  };
  esg_metrics?: {
    esg_score: number;
    carbon_intensity: number;
    esg_rating: string;
    sustainable_investment_pct: number;
  };
  calculation_date: string;
}

export interface CreateFundRequest {
  name: string;
  fund_type: string;
  inception_date: string;
  currency: string;
  domicile: string;
  investment_strategy: string;
  risk_profile: string;
  min_investment: number;
  manager_id: string;
  benchmark?: string;
  description?: string;
  fee_structure: Fund['fee_structure'];
  regulatory_info: Fund['regulatory_info'];
}

export interface UpdateFundRequest extends Partial<CreateFundRequest> {
  id: string;
}

export interface FundFilters {
  fund_type?: string[];
  domicile?: string[];
  currency?: string[];
  manager_id?: string[];
  risk_profile?: string[];
  status?: string[];
  aum_min?: number;
  aum_max?: number;
  inception_after?: string;
  inception_before?: string;
}

export interface BenchmarkComparison {
  fund_id: string;
  benchmark_id: string;
  period: string;
  fund_return: number;
  benchmark_return: number;
  excess_return: number;
  tracking_error: number;
  information_ratio: number;
  correlation: number;
  up_capture: number;
  down_capture: number;
}

// Funds API Service
class FundsApiService {
  private readonly baseUrl = '/funds';

  // Fund Management
  async getFunds(
    filters?: FundFilters,
    page: number = 1,
    size: number = 50
  ): Promise<PaginatedResponse<Fund>> {
    return apiGet(this.baseUrl, {
      params: {
        ...filters,
        page,
        size,
      },
    });
  }

  async getFund(fundId: string): Promise<Fund> {
    return apiGet(`${this.baseUrl}/${fundId}`);
  }

  async createFund(request: CreateFundRequest): Promise<Fund> {
    return apiPost(this.baseUrl, request);
  }

  async updateFund(request: UpdateFundRequest): Promise<Fund> {
    const { id, ...updateData } = request;
    return apiPut(`${this.baseUrl}/${id}`, updateData);
  }

  async deleteFund(fundId: string): Promise<void> {
    return apiDelete(`${this.baseUrl}/${fundId}`);
  }

  // Fund Performance
  async getFundPerformance(
    fundId: string,
    periods?: string[]
  ): Promise<FundPerformance[]> {
    return apiGet(`${this.baseUrl}/${fundId}/performance`, {
      params: { periods: periods?.join(',') },
    });
  }

  async getFundAnalytics(fundId: string): Promise<FundAnalytics> {
    return apiGet(`${this.baseUrl}/${fundId}/analytics`);
  }

  async compareFundPerformance(
    fundIds: string[],
    period: string = '1y'
  ): Promise<{
    funds: Fund[];
    comparison: Array<{
      fund_id: string;
      returns: Record<string, number>;
      risk_metrics: Record<string, number>;
      ranking: Record<string, number>;
    }>;
  }> {
    return apiPost(`${this.baseUrl}/compare`, {
      fund_ids: fundIds,
      period,
    });
  }

  // Fund Holdings
  async getFundHoldings(
    fundId: string,
    asOfDate?: string
  ): Promise<FundHolding[]> {
    return apiGet(`${this.baseUrl}/${fundId}/holdings`, {
      params: { as_of_date: asOfDate },
    });
  }

  async getFundHoldingsHistory(
    fundId: string,
    startDate: string,
    endDate: string
  ): Promise<Record<string, FundHolding[]>> {
    return apiGet(`${this.baseUrl}/${fundId}/holdings/history`, {
      params: { start_date: startDate, end_date: endDate },
    });
  }

  // Benchmark Analysis
  async getBenchmarkComparison(
    fundId: string,
    benchmarkId: string,
    periods?: string[]
  ): Promise<BenchmarkComparison[]> {
    return apiGet(`${this.baseUrl}/${fundId}/benchmark/${benchmarkId}`, {
      params: { periods: periods?.join(',') },
    });
  }

  async getAvailableBenchmarks(): Promise<Array<{
    id: string;
    name: string;
    description: string;
    currency: string;
    asset_class: string;
  }>> {
    return apiGet(`${this.baseUrl}/benchmarks`);
  }

  // Risk Analysis
  async getFundRiskAnalysis(fundId: string): Promise<{
    fund_id: string;
    var_analysis: {
      var_95_1d: number;
      var_99_1d: number;
      expected_shortfall: number;
      volatility: number;
    };
    stress_testing: {
      scenarios: Record<string, {
        scenario: string;
        loss_pct: number;
        description: string;
      }>;
      worst_case_scenario: string;
    };
    concentration_risk: {
      top_holdings_weight: number;
      sector_concentration: Record<string, number>;
      geographic_concentration: Record<string, number>;
    };
    liquidity_risk: {
      liquidity_score: number;
      days_to_liquidate: Record<string, number>;
    };
  }> {
    return apiGet(`${this.baseUrl}/${fundId}/risk-analysis`);
  }

  // Fund Categories and Filters
  async getFundTypes(): Promise<string[]> {
    return apiGet(`${this.baseUrl}/types`);
  }

  async getDomiciles(): Promise<string[]> {
    return apiGet(`${this.baseUrl}/domiciles`);
  }

  async getRiskProfiles(): Promise<string[]> {
    return apiGet(`${this.baseUrl}/risk-profiles`);
  }

  // Search and Discovery
  async searchFunds(
    query: string,
    filters?: FundFilters
  ): Promise<{
    query: string;
    total_results: number;
    funds: Array<Fund & { relevance_score: number }>;
  }> {
    return apiPost(`${this.baseUrl}/search`, {
      query,
      filters,
    });
  }

  async getFundsWithSimilarStrategy(
    fundId: string,
    limit: number = 5
  ): Promise<Array<Fund & { similarity_score: number }>> {
    return apiGet(`${this.baseUrl}/${fundId}/similar`, {
      params: { limit },
    });
  }

  // Reporting and Export
  async getFundReport(
    fundId: string,
    reportType: 'factsheet' | 'performance' | 'holdings' | 'risk',
    format: 'pdf' | 'excel' = 'pdf'
  ): Promise<{ download_url: string; expires_at: string }> {
    return apiPost(`${this.baseUrl}/${fundId}/reports`, {
      report_type: reportType,
      format,
    });
  }

  async exportFundsData(
    fundIds: string[],
    dataTypes: string[],
    format: 'csv' | 'excel' = 'excel'
  ): Promise<{ download_url: string; expires_at: string }> {
    return apiPost(`${this.baseUrl}/export`, {
      fund_ids: fundIds,
      data_types: dataTypes,
      format,
    });
  }

  // Batch operations
  async getFundsOverview(fundIds?: string[]): Promise<{
    total_funds: number;
    total_aum: number;
    by_type: Record<string, { count: number; aum: number }>;
    by_domicile: Record<string, { count: number; aum: number }>;
    performance_summary: {
      ytd_avg: number;
      best_performer: Fund;
      worst_performer: Fund;
    };
    top_funds_by_aum: Fund[];
  }> {
    return apiGet(`${this.baseUrl}/overview`, {
      params: { fund_ids: fundIds?.join(',') },
    });
  }

  async getFundsDashboard(): Promise<{
    summary: {
      total_funds: number;
      total_aum: number;
      avg_performance_ytd: number;
      funds_above_benchmark: number;
    };
    recent_launches: Fund[];
    top_performers: Fund[];
    risk_alerts: Array<{
      fund_id: string;
      fund_name: string;
      alert_type: string;
      severity: string;
      message: string;
    }>;
    aum_trend: Array<{
      date: string;
      total_aum: number;
    }>;
  }> {
    return apiGet(`${this.baseUrl}/dashboard`);
  }
}

// Export singleton instance
export const fundsApi = new FundsApiService();
export default fundsApi;